/**
 * Animations Tab - Spin timing and easing
 */

import type { SkinAnimationConfig } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface AnimationsTabProps {
  config: SkinAnimationConfig;
  onUpdate: (config: SkinAnimationConfig) => void;
}

const EASING_OPTIONS: { value: SkinAnimationConfig['reelEasing']; label: string; description: string }[] = [
  { value: 'linear', label: 'Linear', description: 'Constant speed throughout' },
  { value: 'easeIn', label: 'Ease In', description: 'Starts slow, ends fast' },
  { value: 'easeOut', label: 'Ease Out', description: 'Starts fast, ends slow' },
  { value: 'easeInOut', label: 'Ease In Out', description: 'Slow start and end' },
  { value: 'easeOutBack', label: 'Ease Out Back', description: 'Overshoots slightly then settles' },
  { value: 'easeOutBounce', label: 'Ease Out Bounce', description: 'Bounces at the end' },
  { value: 'easeOutElastic', label: 'Ease Out Elastic', description: 'Elastic bounce effect' },
];

export default function AnimationsTab({ config, onUpdate }: AnimationsTabProps) {
  const updateField = <K extends keyof SkinAnimationConfig>(field: K, value: SkinAnimationConfig[K]) => {
    onUpdate({ ...config, [field]: value });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Animation Configuration</h2>
        <p className="text-sm text-gray-500 mb-6">
          Fine-tune the spin animations, timing, and easing curves.
        </p>
      </div>

      {/* Spin Duration */}
      <div>
        <h3 className="text-md font-medium mb-4">Spin Timing</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spin Duration
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1000}
                max={5000}
                step={100}
                value={config.spinDuration}
                onChange={(e) => updateField('spinDuration', Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-20 text-right">
                {config.spinDuration}ms
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Total duration of the spin animation
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reel Stop Delay
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={50}
                max={500}
                step={25}
                value={config.reelStopDelay}
                onChange={(e) => updateField('reelStopDelay', Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-20 text-right">
                {config.reelStopDelay}ms
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Delay between each reel stopping
            </p>
          </div>
        </div>
      </div>

      {/* Easing */}
      <div>
        <h3 className="text-md font-medium mb-4">Easing Curve</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select how the reels decelerate when stopping.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {EASING_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => updateField('reelEasing', option.value)}
              className={`
                p-4 border rounded-lg text-left transition-all
                ${config.reelEasing === option.value
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs text-gray-500 mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Bounce Effect */}
      <div>
        <h3 className="text-md font-medium mb-4">Bounce Effect</h3>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bounce Intensity
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={0.5}
              step={0.05}
              value={config.reelBounceIntensity}
              onChange={(e) => updateField('reelBounceIntensity', Number(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm text-gray-600 w-16 text-right">
              {(config.reelBounceIntensity * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            How much the reels bounce when they stop (0 = no bounce)
          </p>
        </div>
      </div>

      {/* Anticipation */}
      <div>
        <h3 className="text-md font-medium mb-4">Anticipation Effect</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.anticipation}
              onChange={(e) => updateField('anticipation', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium">Enable Anticipation</span>
              <p className="text-xs text-gray-500">
                Briefly delay the last reel to build suspense
              </p>
            </div>
          </label>

          {config.anticipation && (
            <div className="max-w-md ml-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anticipation Duration
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={50}
                  max={500}
                  step={25}
                  value={config.anticipationDuration || 150}
                  onChange={(e) => updateField('anticipationDuration', Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm text-gray-600 w-20 text-right">
                  {config.anticipationDuration || 150}ms
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visual Preview */}
      <div>
        <h3 className="text-md font-medium mb-4">Timing Preview</h3>
        <div className="bg-gray-100 rounded-lg p-6">
          <div className="flex gap-4 items-end">
            {[1, 2, 3, 4, 5].map((reel) => {
              const delay = (reel - 1) * config.reelStopDelay;
              const totalTime = config.spinDuration + delay;
              const anticipationDelay = config.anticipation && reel === 5
                ? (config.anticipationDuration || 150)
                : 0;

              return (
                <div key={reel} className="flex-1 text-center">
                  <div
                    className="bg-primary-500 rounded-t-lg mx-auto"
                    style={{
                      width: 40,
                      height: Math.max(20, (totalTime + anticipationDelay) / 30),
                    }}
                  />
                  <div className="text-xs text-gray-500 mt-2">
                    Reel {reel}
                  </div>
                  <div className="text-xs text-gray-400">
                    {totalTime + anticipationDelay}ms
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center text-sm text-gray-500 mt-4">
            Total spin time: {config.spinDuration + (4 * config.reelStopDelay) + (config.anticipation ? (config.anticipationDuration || 150) : 0)}ms
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <h3 className="text-md font-medium mb-4">Quick Presets</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onUpdate({
              ...config,
              spinDuration: 2000,
              reelStopDelay: 150,
              reelEasing: 'easeOut',
              reelBounceIntensity: 0.1,
              anticipation: false,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Fast & Smooth
          </button>
          <button
            onClick={() => onUpdate({
              ...config,
              spinDuration: 2500,
              reelStopDelay: 200,
              reelEasing: 'easeOutBack',
              reelBounceIntensity: 0.15,
              anticipation: true,
              anticipationDuration: 150,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Classic Casino
          </button>
          <button
            onClick={() => onUpdate({
              ...config,
              spinDuration: 3000,
              reelStopDelay: 250,
              reelEasing: 'easeOutBounce',
              reelBounceIntensity: 0.25,
              anticipation: true,
              anticipationDuration: 300,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Dramatic
          </button>
          <button
            onClick={() => onUpdate({
              ...config,
              spinDuration: 1500,
              reelStopDelay: 100,
              reelEasing: 'linear',
              reelBounceIntensity: 0,
              anticipation: false,
            })}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Minimal
          </button>
        </div>
      </div>
    </div>
  );
}
